import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/utils/api';
import {
  Activity, AlertTriangle, CheckCircle2, Clock, Plus, Download, ChevronRight,
  Sparkles, Target, Building2, X,
} from 'lucide-react';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Operational Readiness Engine — traffic-light dashboard, project list,
// assessment detail view with per-item answers, AI recommendations.
// Route: /readiness
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  green: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Ready' },
  amber: { bg: 'bg-amber-50',   text: 'text-amber-800',   dot: 'bg-amber-500',   label: 'On track' },
  red:   { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',     label: 'At risk' },
};

export default function Readiness() {
  const [tab, setTab] = useState('dashboard');
  const [dash, setDash] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    try {
      const [d, t] = await Promise.all([api.readinessDashboard(), api.readinessTemplates()]);
      setDash(d.data); setTemplates(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load readiness data');
    }
  };
  useEffect(() => { load(); }, []);

  if (selectedId) return <AssessmentDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} />;

  return (
    <Layout>
      <div className="max-w-[1240px] mx-auto px-6 py-8" data-testid="readiness-page">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0E1320] text-white text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase">
              <Target className="h-3 w-3" /> Operational Readiness
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-[#0E1320]">Are your assets truly ready to operate?</h1>
            <p className="mt-1.5 text-[14px] text-[#4A5263] max-w-2xl">
              Score handover, SOPs, staffing, maintenance, compliance, and tenant experience. Forecast go-live. Close the gaps. Benchmark across projects.
            </p>
          </div>
          <button onClick={() => setOpenCreate(true)} data-testid="readiness-create-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00734A] text-white text-[13px] font-semibold hover:bg-[#005F3D] transition-colors">
            <Plus className="h-4 w-4" /> New assessment
          </button>
        </div>

        {/* KPI strip */}
        {dash && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <KPI label="Projects assessed" val={dash.total_projects} icon={Building2} />
            <KPI label="Average readiness" val={`${dash.average_readiness}%`} icon={Activity} />
            <KPI label="Ready" val={dash.status_counts?.green || 0} icon={CheckCircle2} accent="text-emerald-600" />
            <KPI label="At risk" val={dash.status_counts?.red || 0} icon={AlertTriangle} accent="text-red-600" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E6EAF0] mb-6">
          {[
            { id: 'dashboard', label: 'Projects' },
            { id: 'templates', label: 'Assessment templates' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`readiness-tab-${t.id}`}
              className={`px-4 py-2.5 text-[13px] font-medium transition-colors border-b-2 ${
                tab === t.id ? 'border-[#00734A] text-[#0E1320]' : 'border-transparent text-[#4A5263] hover:text-[#0E1320]'
              }`}>{t.label}</button>
          ))}
        </div>

        {/* Dashboard tab */}
        {tab === 'dashboard' && (
          <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
            {(dash?.projects || []).length === 0 ? (
              <div className="text-center py-16 px-6">
                <Target className="h-10 w-10 mx-auto text-[#71777E]" strokeWidth={1.4} />
                <h3 className="mt-3 text-[15px] font-semibold text-[#0E1320]">No assessments yet</h3>
                <p className="mt-1 text-[12.5px] text-[#4A5263]">Start with a Mega-Development template or Generic Property Handover.</p>
                <button onClick={() => setOpenCreate(true)} data-testid="readiness-empty-cta"
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00734A] text-white text-[13px] font-semibold hover:bg-[#005F3D] transition-colors">
                  <Plus className="h-4 w-4" /> Create first assessment
                </button>
              </div>
            ) : (
              <table className="w-full text-[13px]">
                <thead className="bg-[#FBFCFD] text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
                  <tr>
                    <th className="text-start px-4 py-3 font-medium">Project</th>
                    <th className="text-start px-4 py-3 font-medium">Template</th>
                    <th className="text-start px-4 py-3 font-medium">Readiness</th>
                    <th className="text-start px-4 py-3 font-medium">Risk</th>
                    <th className="text-start px-4 py-3 font-medium">Maturity</th>
                    <th className="text-start px-4 py-3 font-medium">Status</th>
                    <th className="text-start px-4 py-3 font-medium">Forecast</th>
                    <th className="text-end px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {dash.projects.map(p => {
                    const s = STATUS_STYLE[p.status] || STATUS_STYLE.red;
                    return (
                      <tr key={p.id} className="border-t border-[#F1F4F7] hover:bg-[#FBFCFD] cursor-pointer" onClick={() => setSelectedId(p.id)} data-testid={`readiness-row-${p.id}`}>
                        <td className="px-4 py-3 text-[#0E1320] font-medium">{p.project_name}</td>
                        <td className="px-4 py-3 text-[#4A5263]">{p.template_name_en}</td>
                        <td className="px-4 py-3 text-[#0E1320] reva-mono">{p.readiness_score}%</td>
                        <td className="px-4 py-3 text-[#0E1320] reva-mono">{p.risk_score}</td>
                        <td className="px-4 py-3 text-[#0E1320] reva-mono">{p.maturity_score}%</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${s.bg} ${s.text} text-[11px] font-medium`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {s.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#4A5263] reva-mono">{p.go_live_forecast || '—'}</td>
                        <td className="px-4 py-3 text-end"><ChevronRight className="h-4 w-4 text-[#71777E]" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Templates tab */}
        {tab === 'templates' && (
          <div className="grid md:grid-cols-2 gap-3">
            {templates.map(t => (
              <div key={t.id} className="rounded-xl border border-[#E6EAF0] bg-white p-5" data-testid={`readiness-template-${t.id}`}>
                <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">{t.is_system ? 'System template' : 'Custom'}</div>
                <h3 className="mt-2 text-[16px] font-semibold text-[#0E1320]">{t.name_en}</h3>
                <p className="mt-1.5 text-[12.5px] text-[#4A5263]">{t.description_en}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {t.dimensions?.map(d => (
                    <span key={d.id} className="px-2 py-0.5 rounded-full bg-[#F1F4F7] text-[11px] text-[#0E1320]">{d.name_en}</span>
                  ))}
                </div>
                <div className="mt-3 text-[11.5px] text-[#71777E] reva-mono">{t.items?.length || 0} items · weighted scoring</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {openCreate && (
        <CreateModal templates={templates} onClose={() => setOpenCreate(false)} onCreated={(id) => { setOpenCreate(false); setSelectedId(id); }} />
      )}
    </Layout>
  );
}

// ─── KPI card ───────────────────────────────────────────────────────────
function KPI({ label, val, icon: Icon, accent = 'text-[#00734A]' }) {
  return (
    <div className="rounded-xl border border-[#E6EAF0] bg-white p-4" data-testid={`readiness-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <Icon className={`h-4 w-4 ${accent}`} strokeWidth={1.7} />
        <span className="text-[10px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{label}</span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-[#0E1320]">{val}</div>
    </div>
  );
}

// ─── Create modal ───────────────────────────────────────────────────────
function CreateModal({ templates, onClose, onCreated }) {
  const [tpl, setTpl] = useState(templates[0]?.id || '');
  const [name, setName] = useState('');
  const [go, setGo] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!tpl || !name) return;
    setBusy(true);
    try {
      const { data } = await api.readinessCreate({ template_id: tpl, project_name: name, target_go_live: go || null });
      toast.success('Assessment created');
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-[#0E1320]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6" data-testid="readiness-create-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">New assessment</div>
            <h2 className="mt-1 text-xl font-semibold text-[#0E1320]">Create operational readiness assessment</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[#71777E] hover:text-[#0E1320]"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="text-[12px] text-[#4A5263]">Project name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="readiness-create-name"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[14px]" placeholder="Diriyah Heritage — Phase 1" />
          </label>
          <label className="block">
            <span className="text-[12px] text-[#4A5263]">Template</span>
            <select value={tpl} onChange={(e) => setTpl(e.target.value)} required data-testid="readiness-create-template"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[14px]">
              {templates.map(t => <option key={t.id} value={t.id}>{t.name_en}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[12px] text-[#4A5263]">Target go-live (optional)</span>
            <input type="date" value={go} onChange={(e) => setGo(e.target.value)} data-testid="readiness-create-go-live"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[14px]" />
          </label>
        </div>
        <button disabled={busy || !name || !tpl} type="submit" data-testid="readiness-create-submit"
          className="mt-5 w-full h-11 rounded-xl bg-[#00734A] hover:bg-[#005F3D] text-white text-[14px] font-semibold disabled:opacity-60">
          {busy ? 'Creating…' : 'Create assessment'}
        </button>
      </form>
    </div>
  );
}

// ─── Assessment detail ──────────────────────────────────────────────────
function AssessmentDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [recs, setRecs] = useState(null);
  const load = async () => {
    try { const { data } = await api.readinessGet(id); setData(data); }
    catch (e) { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setAnswer = async (itemId, value) => {
    try {
      const { data: updated } = await api.readinessAnswer(id, { item_id: itemId, value });
      setData(prev => ({ ...prev, ...updated }));
    } catch (e) { toast.error('Save failed'); }
  };

  const generateRecs = async () => {
    try {
      const { data } = await api.readinessRecommend(id);
      setRecs(data.recommendations);
      toast.success(`${data.count} recommendations generated`);
    } catch (e) { toast.error('Recommendation generation failed'); }
  };

  if (!data) return <Layout><div className="max-w-[1240px] mx-auto px-6 py-8 text-[#71777E]">Loading…</div></Layout>;

  const s = STATUS_STYLE[data.status] || STATUS_STYLE.red;
  const groupedItems = {};
  (data.template?.items || []).forEach(it => { (groupedItems[it.dim] ||= []).push(it); });

  return (
    <Layout>
      <div className="max-w-[1240px] mx-auto px-6 py-8" data-testid={`readiness-detail-${id}`}>
        <button onClick={onBack} className="text-[12px] text-[#4A5263] hover:text-[#0E1320] mb-4" data-testid="readiness-detail-back">← All projects</button>

        <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
          <div>
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">{data.template_name_en}</div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-[#0E1320]">{data.project_name}</h1>
            <div className="mt-2 inline-flex items-center gap-2 text-[12px] text-[#4A5263]">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${s.bg} ${s.text} font-medium`}>
                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {s.label}
              </span>
              {data.go_live_forecast && <span className="reva-mono">Forecast: {data.go_live_forecast}</span>}
              {data.target_go_live && <span className="reva-mono">Target: {data.target_go_live}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={generateRecs} data-testid="readiness-recommend-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E6EAF0] text-[#0E1320] text-[13px] font-medium hover:border-[#0E1320]">
              <Sparkles className="h-4 w-4 text-[#00734A]" /> Generate recommendations
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0E1320] text-white text-[13px] font-medium hover:bg-[#1A2238]" data-testid="readiness-export-btn">
              <Download className="h-4 w-4" /> Executive PDF
            </button>
          </div>
        </div>

        {/* Score band */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <KPI label="Readiness" val={`${data.readiness_score}%`} icon={Activity} />
          <KPI label="Risk score" val={data.risk_score} icon={AlertTriangle} accent="text-red-600" />
          <KPI label="Maturity" val={`${data.maturity_score}%`} icon={Target} />
          <KPI label="Critical gaps" val={data.critical_gaps} icon={Clock} accent="text-amber-600" />
        </div>

        {/* Dimension breakdown */}
        <div className="grid md:grid-cols-2 gap-3 mb-8">
          {(data.dimensions || []).map(d => (
            <div key={d.id} className="rounded-xl border border-[#E6EAF0] bg-white p-4" data-testid={`readiness-dim-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold text-[#0E1320]">{d.name_en}</div>
                <div className="text-[12px] reva-mono text-[#4A5263]">{d.items_done}/{d.items_total}</div>
              </div>
              <div className="mt-2 h-2 rounded-full bg-[#F1F4F7] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${d.score}%`, background: d.score >= 85 ? '#10B981' : d.score >= 60 ? '#F59E0B' : '#EF4444' }} />
              </div>
              <div className="mt-1 text-[11px] reva-mono text-[#71777E]">{d.score}%</div>
            </div>
          ))}
        </div>

        {/* Items by dimension */}
        <div className="space-y-6">
          {(data.template?.dimensions || []).map(dim => (
            <div key={dim.id} className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
              <div className="px-4 py-3 bg-[#FBFCFD] border-b border-[#E6EAF0]">
                <h3 className="text-[13px] font-semibold text-[#0E1320]">{dim.name_en}</h3>
              </div>
              <ul className="divide-y divide-[#F1F4F7]">
                {(groupedItems[dim.id] || []).map(it => {
                  const ans = data.answers?.[it.id]?.value || 'no';
                  return (
                    <li key={it.id} className="px-4 py-3 flex items-start gap-3" data-testid={`readiness-item-${it.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-[#0E1320] font-medium flex items-center gap-2">
                          {it.text_en}
                          {it.critical && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[9.5px] font-bold reva-mono uppercase tracking-[0.12em]">Critical</span>}
                          <span className="ml-auto text-[11px] reva-mono text-[#71777E]">w{it.weight}</span>
                        </div>
                        <div className="text-[11.5px] text-[#71777E] mt-0.5">{it.text_ar}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {['yes', 'partial', 'no', 'na'].map(v => (
                          <button key={v} onClick={() => setAnswer(it.id, v)} data-testid={`readiness-answer-${it.id}-${v}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                              ans === v
                                ? (v === 'yes' ? 'bg-emerald-600 text-white border-emerald-600' :
                                   v === 'partial' ? 'bg-amber-500 text-white border-amber-500' :
                                   v === 'no' ? 'bg-red-600 text-white border-red-600' :
                                   'bg-[#0E1320] text-white border-[#0E1320]')
                                : 'bg-white text-[#0E1320] border-[#E6EAF0] hover:border-[#0E1320]'
                            }`}>
                            {v === 'yes' ? 'Yes' : v === 'partial' ? 'Partial' : v === 'no' ? 'No' : 'N/A'}
                          </button>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Recommendations */}
        {recs && (
          <div className="mt-8 rounded-2xl border border-[#E6EAF0] bg-white p-5" data-testid="readiness-recs-panel">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[#00734A]" />
              <h3 className="text-[15px] font-semibold text-[#0E1320]">Recommendations · {recs.length}</h3>
            </div>
            <ul className="divide-y divide-[#F1F4F7]">
              {recs.map((r, i) => (
                <li key={i} className="py-3 flex items-start gap-3" data-testid={`readiness-rec-${r.item_id}`}>
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium uppercase reva-mono ${
                    r.priority === 'critical' ? 'bg-red-100 text-red-800' :
                    r.priority === 'high' ? 'bg-amber-100 text-amber-800' : 'bg-[#F1F4F7] text-[#0E1320]'
                  }`}>{r.priority}</span>
                  <div className="flex-1 text-[12.5px]">
                    <div className="text-[#0E1320] font-medium">{r.text_en}</div>
                    <div className="mt-1 text-[#4A5263]">{r.recommendation_en}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}
